<script lang="ts" setup>
import { Theme } from '@/composition/themes'
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { computed, ref } from 'vue'

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
  likeAction: {
    type: Function,
    required: true,
  },
})

const hapticsImpactMedium = async () => {
  if (Capacitor.isNativePlatform()) {
    await Haptics.impact({ style: ImpactStyle.Medium })
  }
}

const isActiveLike = computed(() => {
  return props.content?.current_user_reactions?.LIKE ? true : false
})

const likeIcon = ref()
function scaleLikeIcon() {
  hapticsImpactMedium()
  props.likeAction()
  if (props.content?.current_user_reactions?.LIKE) {
    if (likeIcon.value) {
      likeIcon.value.style.transform =
        'scale(1.25) rotate(-20deg) translateX(-10px)'
      setTimeout(
        () =>
          (likeIcon.value.style.transform =
            'scale(1) rotate(0deg) translateX(-10px)  '),
        300
      )
    }
  }
}
</script>
<template>
  <div
    id="like_post_button"
    ref="likeIcon"
    class="flex h-10 w-10 -translate-x-2.5 scale-100 transform cursor-pointer items-center justify-center overflow-visible !font-bold text-communities-font-secondary transition-transform duration-200 hover:rounded-full hover:bg-communities-fill hover:text-communities-font-primary"
    tabindex="0"
    role="button"
    aria-label="Like content"
    @click.stop.prevent="scaleLikeIcon"
    @keyup.enter="scaleLikeIcon"
  >
    <svg
      v-if="!isActiveLike"
      class="h-[17px] w-[17px]"
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
    >
      <path
        d="M13.7595 9.36801C13.5194 10.0515 12.8824 10.671 12.2177 11.1748C11.1505 11.9837 10.422 13.1487 10.422 14.4878L10.4219 19.8073V25.2828C10.4219 26.5351 11.0851 27.5887 12.2797 27.9643C14.1862 28.5636 17.349 29.368 20.8317 29.6873C27.1727 30.2686 27.9012 27.5068 28.2841 26.0557L28.2935 26.0197C28.6741 24.5788 29.1378 20.8485 29.4889 17.9668C29.84 15.0851 28.5613 13.5519 26.8515 12.5951C24.4571 11.2553 21.8688 12.3439 20.6005 12.5951C21.0804 11.236 21.7652 8.667 21.4029 6.674C21.1012 5.01502 20.651 2.97335 18.2451 2.40329C15.8393 1.83323 15.076 2.79947 14.5059 3.79466C13.9359 4.78984 14.3876 7.58054 13.7595 9.36801Z"
        stroke="currentColor"
        stroke-width="2.4"
      />
      <path
        d="M10.4051 24.5923V14.0873C10.4051 11.8914 8.62501 10.1113 6.42912 10.1113C4.23323 10.1113 2.45312 11.8914 2.45312 14.0873V24.5923C2.45312 26.7882 4.23323 28.5683 6.42912 28.5683C8.62501 28.5683 10.4051 26.7882 10.4051 24.5923Z"
        stroke="currentColor"
        stroke-width="2.4"
      />
    </svg>
    <svg
      v-else
      class="h-[17px] w-[17px]"
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="currentColor"
    >
      <path
        :class="
          Theme.state.isDarkMode
            ? 'fill-communities-primary-light text-communities-primary-light'
            : 'fill-communities-primary-dark text-communities-primary-dark'
        "
        d="M13.241 8.81554C12.9636 9.60514 12.1973 10.3159 11.4272 10.8799C10.3467 11.6711 9.62505 12.8444 9.62504 14.1836L9.62502 20.1255L9.625 26.2234C9.625 27.4756 10.2891 28.5322 11.4819 28.9133C13.5282 29.567 17.0333 30.4746 20.903 30.8294C27.7727 31.4592 28.5621 28.4671 28.9830 26.8949L28.9871 26.856C29.3994 25.2949 29.9017 21.2535 30.2821 18.1315C30.6625 15.0095 29.2772 13.3483 27.4248 12.3118C24.8307 10.8602 22.0265 12.0396 20.6525 12.3118C21.1724 10.8393 21.9143 8.05608 21.5218 5.89687C21.195 4.09954 20.7071 1.88759 18.1007 1.27C15.4942 0.652397 14.6672 1.69922 14.0497 2.7774C13.4321 3.85558 13.9214 6.87901 13.241 8.81554Z"
        fill="currentColor"
        stroke="currentColor"
        stroke-width="2.4"
      />
      <path
        class="fill-communities-background"
        :class="
          Theme.state.isDarkMode
            ? 'text-communities-primary-light'
            : 'text-communities-primary-dark'
        "
        d="M10.4052 24.5923V14.0873C10.4052 11.8914 8.62502 10.1113 6.42913 10.1113C4.23325 10.1113 2.45312 11.8914 2.45312 14.0873V24.5923C2.45312 26.7882 4.23325 28.5683 6.42913 28.5683C8.62502 28.5683 10.4052 26.7882 10.4052 24.5923Z"
        fill="currentColor"
        stroke="currentColor"
        stroke-width="2.4"
      />
    </svg>
  </div>
</template>
